import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CloudRain, CheckCircle, FileArrowDown, ShareNetwork, Spinner } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const queryId = searchParams.get('query_id');
  const [status, setStatus] = useState('verifying');
  const [reportId, setReportId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      attempts++;
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/payments/status/${sessionId}`,
          { withCredentials: true }
        );

        if (data.payment_status === 'paid') {
          setStatus('paid');
          if (queryId) {
            generateReport();
          }
          return;
        }
      } catch (error) {
        // Ignore polling errors
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setStatus('timeout');
      }
    };

    poll();
  };

  const generateReport = async () => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/weather/report/preview?query_id=${queryId}`,
        {},
        { withCredentials: true }
      );
      setReportId(data.report_id);
    } catch (error) {
      // Report may already exist
    }
  };

  const handleDownload = async () => {
    if (!reportId) return;
    setDownloading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/reports/download/${reportId}`,
        { withCredentials: true }
      );
      const byteArray = new Uint8Array(data.pdf_data.match(/.{1,2}/g).map(b => parseInt(b, 16)));
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WeatherProof-${reportId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Download failed. Check My Reports page.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <CloudRain size={28} weight="bold" className="text-blue-600" />
            <span className="text-xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        {status === 'verifying' && (
          <div className="text-center py-12" data-testid="payment-verifying">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>Verifying Payment</h1>
            <p className="text-sm text-slate-500">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'paid' && (
          <div className="text-center" data-testid="payment-success">
            <CheckCircle size={64} weight="fill" className="text-emerald-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>Payment Successful</h1>
            <p className="text-sm text-slate-500 mb-8">Your weather report is ready for download.</p>

            {reportId ? (
              <div className="space-y-3">
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  data-testid="download-report-btn"
                  className="w-full max-w-sm mx-auto px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <FileArrowDown size={20} weight="bold" />
                  {downloading ? 'Downloading...' : 'Download PDF Report'}
                </button>
                <p className="text-xs text-slate-400 font-mono">Report ID: {reportId}</p>
              </div>
            ) : (
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            )}

            <div className="mt-10 flex items-center justify-center gap-4">
              <Link to="/reports" className="text-sm text-blue-600 hover:text-blue-700 font-medium">My Reports</Link>
              <span className="text-slate-300">|</span>
              <Link to="/lookup" className="text-sm text-blue-600 hover:text-blue-700 font-medium">New Lookup</Link>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'timeout') && (
          <div className="text-center py-12" data-testid="payment-error">
            <h1 className="text-2xl font-bold text-slate-950 mb-2" style={{ fontFamily: 'Chivo' }}>Payment Verification Issue</h1>
            <p className="text-sm text-slate-500 mb-6">
              {status === 'timeout'
                ? 'Payment verification timed out. If you were charged, your report will appear in My Reports shortly.'
                : 'Something went wrong verifying your payment.'}
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/reports" className="px-6 py-2 bg-slate-950 text-white text-sm font-medium hover:bg-slate-800 transition-colors">Check My Reports</Link>
              <Link to="/lookup" className="text-sm text-blue-600 hover:text-blue-700 font-medium">Back to Lookup</Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PaymentSuccess;
